/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import { useAlertDialog } from '../common/AlertDialogContext';
import { useTheme } from '../../theme/ThemeContext';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

// ── helpers ─────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function getExt(file) {
  const name = file?.filename || file?.display_name || '';
  if (!name.includes('.')) return '';
  return name.split('.').pop().toLowerCase();
}

const FILE_TYPE_META = {
  image:    { icon: 'bi-image',          color: '#6366f1', bg: '#eef2ff' },
  video:    { icon: 'bi-camera-video',   color: '#0891b2', bg: '#ecfeff' },
  audio:    { icon: 'bi-music-note',     color: '#7c3aed', bg: '#f5f3ff' },
  document: { icon: 'bi-file-earmark-text', color: '#dc2626', bg: '#fef2f2' },
  text:     { icon: 'bi-file-text',      color: '#059669', bg: '#ecfdf5' },
  archive:  { icon: 'bi-archive',        color: '#d97706', bg: '#fffbeb' },
  other:    { icon: 'bi-file-earmark',   color: '#64748b', bg: '#f8fafc' },
};

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'image', label: 'Images' },
  { value: 'document', label: 'Documents' },
  { value: 'video', label: 'Videos' },
  { value: 'audio', label: 'Audio' },
  { value: 'text', label: 'Text' },
  { value: 'archive', label: 'Archives' },
  { value: 'other', label: 'Other' },
];

// ── main component ───────────────────────────────────────────────────────────

export default function FileBrowserComponent({ applicationId }) {
  const { theme } = useTheme();
  const qc = useQueryClient();
  const { showAlertDialog } = useAlertDialog();

  const [page, setPage]               = useState(1);
  const [search, setSearch]           = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [fileType, setFileType]       = useState('');
  const [sortBy, setSortBy]           = useState('uploaded_at');
  const [sortDir, setSortDir]         = useState('desc');
  const [uploadOpen, setUploadOpen]   = useState(false);
  const [copiedId, setCopiedId]       = useState(null);
  const [viewMode, setViewMode]       = useState('grid');   // 'grid' | 'list'
  const [previewFile, setPreviewFile] = useState(null);     // file object to preview

  const PAGE_SIZE = 10;
  const queryKey = ['appFiles', applicationId, page, PAGE_SIZE, search, fileType, sortBy, sortDir];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        page, page_size: PAGE_SIZE,
        sort_by: sortBy, sort_dir: sortDir,
      });
      if (search)   params.set('search', search);
      if (fileType) params.set('file_type', fileType);
      const resp = await apiHelper.apiCall(
        `/arcee/application/${applicationId}/files/?${params}`,
        { method: 'GET', signal }
      );
      if (!resp.ok) throw new Error(`Failed to load files (${resp.status})`);
      return resp.json();
    },
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });

  const files      = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  // ── search ──────────────────────────────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  // ── copy link ───────────────────────────────────────────────────────────
  const handleCopyLink = useCallback(async (file) => {
    try {
      const resp = await apiHelper.apiCall(
        `/arcee/application/${applicationId}/files/${file.file_id}/link/`,
        { method: 'GET' }
      );
      const body = await resp.json();
      const url = body.public_url || file.public_url || '';
      if (!url) { showAlertDialog({ title: 'No Link', message: 'This file does not have a public link.', type: 'info', okLabel: 'OK', showCancel: false }); return; }
      await navigator.clipboard.writeText(url);
      setCopiedId(file.file_id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      showAlertDialog({ title: 'Copy Failed', message: 'Could not copy link. Please try again.', type: 'error', okLabel: 'OK', showCancel: false });
    }
  }, [applicationId, showAlertDialog]);

  // ── delete ──────────────────────────────────────────────────────────────
  const handleDelete = useCallback((file) => {
    showAlertDialog({
      title: 'Delete File',
      message: `Delete "${file.display_name}"? This cannot be undone.`,
      type: 'info',
      okLabel: 'Delete',
      cancelLabel: 'Cancel',
      showCancel: true,
      onOk: async () => {
        try {
          const resp = await apiHelper.apiCall(
            `/arcee/application/${applicationId}/files/${file.file_id}/`,
            { method: 'DELETE' }
          );
          if (resp.ok || resp.status === 204) {
            qc.invalidateQueries({ queryKey: ['appFiles', applicationId] });
            return;
          }
          // Surface the server's reason (e.g. referenced by a customer table row)
          let serverMessage = 'Could not delete the file. Please try again.';
          try {
            const body = await resp.json();
            if (body?.error) serverMessage = body.error;
          } catch { /* response had no JSON body */ }
          showAlertDialog({
            title: resp.status === 403 ? 'Delete Not Allowed' : 'Delete Failed',
            message: serverMessage,
            type: resp.status === 403 ? 'info' : 'error',
            okLabel: 'OK',
            showCancel: false,
          });
        } catch {
          showAlertDialog({ title: 'Delete Failed', message: 'Could not delete the file. Please try again.', type: 'error', okLabel: 'OK', showCancel: false });
        }
      },
    });
  }, [applicationId, showAlertDialog, qc]);

  // ── sort toggle ─────────────────────────────────────────────────────────
  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
    setPage(1);
  };

  const card    = theme?.cardRadius || '12px';
  const btn     = theme?.buttonRadius || '8px';
  const primary = theme?.primary     || '#1e293b';

  return (
    <div>
      {/* ── Toolbar ─────────────────────────────────────────────────── */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <form className="d-flex gap-2 flex-wrap align-items-center" onSubmit={handleSearch}>
          <div className="input-group" style={{ maxWidth: 260 }}>
            <span className="input-group-text bg-white border-end-0">
              <i className="bi bi-search text-muted" style={{ fontSize: 13 }} />
            </span>
            <input
              type="text"
              className="form-control border-start-0 ps-0"
              placeholder="Search files…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              style={{ fontSize: 13 }}
            />
          </div>

          <select
            className="form-select"
            style={{ width: 'auto', fontSize: 13 }}
            value={fileType}
            onChange={e => { setFileType(e.target.value); setPage(1); }}
          >
            {TYPE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <div className="d-flex gap-1">
            {[
              { col: 'uploaded_at', label: 'Date' },
              { col: 'display_name', label: 'Name' },
              { col: 'file_size', label: 'Size' },
            ].map(({ col, label }) => (
              <button
                key={col}
                type="button"
                onClick={() => toggleSort(col)}
                className="btn btn-sm"
                style={{
                  fontSize: 12, fontWeight: 500,
                  background: sortBy === col ? primary : '#f1f5f9',
                  color: sortBy === col ? '#fff' : '#475569',
                  border: 'none',
                  borderRadius: btn,
                }}
              >
                {label}
                {sortBy === col && (
                  <i className={`bi bi-arrow-${sortDir === 'desc' ? 'down' : 'up'} ms-1`} style={{ fontSize: 10 }} />
                )}
              </button>
            ))}
          </div>
        </form>

        {/* Right side: view toggle + upload */}
        <div className="d-flex align-items-center gap-2">
          {/* Grid / List toggle */}
          <div
            className="d-flex"
            style={{
              background: '#f1f5f9',
              borderRadius: btn,
              padding: 3,
              gap: 2,
            }}
          >
            <button
              type="button"
              title="Grid view"
              onClick={() => setViewMode('grid')}
              className="btn btn-sm d-flex align-items-center justify-content-center"
              style={{
                width: 32, height: 32, padding: 0,
                background: viewMode === 'grid' ? primary : 'transparent',
                color: viewMode === 'grid' ? '#fff' : '#64748b',
                border: 'none',
                borderRadius: `calc(${btn} - 2px)`,
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              <i className="bi bi-grid-3x3-gap" style={{ fontSize: 14 }} />
            </button>
            <button
              type="button"
              title="List view"
              onClick={() => setViewMode('list')}
              className="btn btn-sm d-flex align-items-center justify-content-center"
              style={{
                width: 32, height: 32, padding: 0,
                background: viewMode === 'list' ? primary : 'transparent',
                color: viewMode === 'list' ? '#fff' : '#64748b',
                border: 'none',
                borderRadius: `calc(${btn} - 2px)`,
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              <i className="bi bi-list-ul" style={{ fontSize: 14 }} />
            </button>
          </div>

          <button
            className="btn d-flex align-items-center gap-2"
            onClick={() => setUploadOpen(true)}
            style={{
              background: primary,
              color: '#fff',
              borderRadius: btn,
              fontSize: 13,
              fontWeight: 600,
              padding: '8px 16px',
              border: 'none',
            }}
          >
            <i className="bi bi-cloud-upload" />
            Upload File
          </button>
        </div>
      </div>

      {/* ── File Grid / List ─────────────────────────────────────────── */}
      {isLoading && (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border spinner-border-sm text-secondary me-2" />
          <span className="text-muted small">Loading files…</span>
        </div>
      )}

      {!isLoading && error && (
        <div className="alert alert-danger d-flex align-items-center gap-2 py-2 small">
          <i className="bi bi-exclamation-triangle" />
          Failed to load files. Please try again.
        </div>
      )}

      {!isLoading && !error && files.length === 0 && (
        <div className="text-center py-5">
          <i className="bi bi-folder2-open" style={{ fontSize: 48, color: '#cbd5e1' }} />
          <div className="mt-3 text-muted small fw-semibold">No files yet</div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            Upload your first file using the button above.
          </div>
        </div>
      )}

      {!isLoading && !error && files.length > 0 && (
        <>
          {viewMode === 'grid' ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 16,
              }}
            >
              {files.map(file => (
                <FileCard
                  key={file.file_id}
                  file={file}
                  copied={copiedId === file.file_id}
                  onCopy={handleCopyLink}
                  onDelete={handleDelete}
                  onPreview={setPreviewFile}
                  cardRadius={card}
                  btnRadius={btn}
                />
              ))}
            </div>
          ) : (
            <div
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: card,
                overflow: 'hidden',
              }}
            >
              {/* List header */}
              <div
                className="d-flex align-items-center px-3 py-2"
                style={{
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                <div style={{ flex: '1 1 0', minWidth: 0 }}>Name</div>
                <div style={{ width: 90, textAlign: 'right' }}>Size</div>
                <div style={{ width: 110, textAlign: 'right' }}>Date</div>
                <div style={{ width: 128, textAlign: 'right' }}>Actions</div>
              </div>

              {files.map((file, idx) => (
                <FileListRow
                  key={file.file_id}
                  file={file}
                  copied={copiedId === file.file_id}
                  onCopy={handleCopyLink}
                  onDelete={handleDelete}
                  onPreview={setPreviewFile}
                  isLast={idx === files.length - 1}
                  btnRadius={btn}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <span className="text-muted small">
                {totalCount} file{totalCount !== 1 ? 's' : ''} — page {page} of {totalPages}
              </span>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <i className="bi bi-chevron-left" />
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Upload Modal ────────────────────────────────────────────── */}
      {uploadOpen && (
        <UploadModal
          applicationId={applicationId}
          onClose={() => setUploadOpen(false)}
          onSuccess={() => {
            setUploadOpen(false);
            qc.invalidateQueries({ queryKey: ['appFiles', applicationId] });
          }}
          cardRadius={card}
          btnRadius={btn}
          theme={theme}
        />
      )}

      {/* ── File Preview Modal ───────────────────────────────────────── */}
      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          cardRadius={card}
          btnRadius={btn}
          theme={theme}
        />
      )}
    </div>
  );
}

// ── FileCard ─────────────────────────────────────────────────────────────────

function FileCard({ file, copied, onCopy, onDelete, onPreview, cardRadius, btnRadius }) {
  const meta       = FILE_TYPE_META[file.file_type] || FILE_TYPE_META.other;
  const ext        = getExt(file);
  const isCustomer = file.source === 'client_form';

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: cardRadius,
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s, transform 0.15s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.09)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'none';
      }}
    >
      {/* Thumbnail / icon area — click to preview */}
      <div
        role="button"
        tabIndex={0}
        title="Preview file"
        onClick={() => onPreview(file)}
        onKeyDown={e => e.key === 'Enter' && onPreview(file)}
        style={{
          background: meta.bg,
          height: 90,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          position: 'relative',
        }}
      >
        {file.file_type === 'image' && file.public_url ? (
          <img
            src={file.public_url}
            alt={file.display_name}
            style={{ maxHeight: 86, maxWidth: '100%', objectFit: 'cover', width: '100%', height: 90 }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <i className={`bi ${meta.icon}`} style={{ fontSize: 36, color: meta.color }} />
        )}
        {/* Extension badge */}
        {ext && (
          <span
            style={{
              position: 'absolute', top: 6, right: 6,
              fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
              padding: '2px 6px', borderRadius: `calc(${btnRadius} / 2)`,
              background: 'rgba(255,255,255,0.95)',
              color: meta.color,
              textTransform: 'uppercase',
              lineHeight: 1.2,
              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
            }}
          >
            {ext}
          </span>
        )}
        {/* Preview hover overlay */}
        <div
          className="preview-overlay"
          style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = 1}
          onMouseLeave={e => e.currentTarget.style.opacity = 0}
        >
          <i className="bi bi-eye" style={{ fontSize: 22, color: '#fff' }} />
        </div>
      </div>

      {/* Info */}
      <div className="p-2 flex-grow-1" style={{ minWidth: 0 }}>
        <div
          title={file.display_name}
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#1e293b',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {file.display_name}
        </div>
        <div className="d-flex justify-content-between mt-1">
          <span style={{ fontSize: 11, color: '#94a3b8' }}>{formatBytes(file.file_size)}</span>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>{formatDate(file.uploaded_at)}</span>
        </div>
      </div>

      {/* Actions */}
      <div
        className="d-flex border-top"
        style={{ borderColor: '#f1f5f9' }}
      >
        <button
          onClick={() => onPreview(file)}
          title="Preview file"
          className="btn d-flex align-items-center justify-content-center"
          style={{
            fontSize: 13,
            color: '#475569',
            background: 'transparent',
            border: 'none',
            borderRight: '1px solid #f1f5f9',
            borderRadius: 0,
            padding: '7px 10px',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <i className="bi bi-eye" />
        </button>

        <a
          href={file.public_url ? `${file.public_url}?dl=1` : '#'}
          title="Download file"
          className="btn d-flex align-items-center justify-content-center"
          style={{
            fontSize: 13,
            color: '#475569',
            background: 'transparent',
            border: 'none',
            borderRight: '1px solid #f1f5f9',
            borderRadius: 0,
            padding: '7px 10px',
            transition: 'background 0.15s',
            textDecoration: 'none',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <i className="bi bi-download" />
        </a>

        <button
          onClick={() => onCopy(file)}
          title="Copy public link"
          className="btn flex-fill d-flex align-items-center justify-content-center gap-1"
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: copied ? '#059669' : '#475569',
            background: 'transparent',
            border: 'none',
            borderRight: '1px solid #f1f5f9',
            borderRadius: 0,
            padding: '7px 4px',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <i className={`bi ${copied ? 'bi-check2' : 'bi-link-45deg'}`} style={{ fontSize: 13 }} />
          {copied ? 'Copied!' : 'Copy Link'}
        </button>

        <button
          onClick={() => !isCustomer && onDelete(file)}
          aria-disabled={isCustomer}
          title={isCustomer
            ? "Customer upload — delete the table row that uses this file to remove it"
            : "Delete file"}
          className="btn d-flex align-items-center justify-content-center"
          style={{
            fontSize: 13,
            color: isCustomer ? '#cbd5e1' : '#dc2626',
            background: 'transparent',
            border: 'none',
            borderRadius: 0,
            padding: '7px 10px',
            transition: 'background 0.15s',
            cursor: isCustomer ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={e => { if (!isCustomer) e.currentTarget.style.background = '#fef2f2'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <i className={`bi ${isCustomer ? 'bi-lock-fill' : 'bi-trash3'}`} />
        </button>
      </div>
    </div>
  );
}

// ── FileListRow ───────────────────────────────────────────────────────────────

function FileListRow({ file, copied, onCopy, onDelete, onPreview, isLast, btnRadius }) {
  const meta       = FILE_TYPE_META[file.file_type] || FILE_TYPE_META.other;
  const ext        = getExt(file);
  const isCustomer = file.source === 'client_form';

  return (
    <div
      className="d-flex align-items-center px-3 py-2"
      style={{
        borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {/* Icon + name */}
      <div
        className="d-flex align-items-center gap-2 flex-fill"
        style={{ minWidth: 0, cursor: 'pointer' }}
        onClick={() => onPreview(file)}
        title="Preview file"
      >
        <div
          style={{
            width: 32, height: 32,
            background: meta.bg,
            borderRadius: btnRadius,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {file.file_type === 'image' && file.public_url ? (
            <img
              src={file.public_url}
              alt=""
              style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: btnRadius }}
              onError={e => { e.target.style.display = 'none'; }}
            />
          ) : (
            <i className={`bi ${meta.icon}`} style={{ fontSize: 15, color: meta.color }} />
          )}
        </div>
        <span
          style={{
            fontSize: 13, fontWeight: 500, color: '#1e293b',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
          title={file.display_name}
        >
          {file.display_name}
        </span>
        {ext && (
          <span
            style={{
              flexShrink: 0,
              fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
              padding: '2px 6px', borderRadius: `calc(${btnRadius} / 2)`,
              background: meta.bg, color: meta.color,
              textTransform: 'uppercase', lineHeight: 1.2,
            }}
          >
            {ext}
          </span>
        )}
      </div>

      {/* Size */}
      <div style={{ width: 90, textAlign: 'right', fontSize: 12, color: '#94a3b8', flexShrink: 0 }}>
        {formatBytes(file.file_size)}
      </div>

      {/* Date */}
      <div style={{ width: 110, textAlign: 'right', fontSize: 12, color: '#94a3b8', flexShrink: 0 }}>
        {formatDate(file.uploaded_at)}
      </div>

      {/* Actions */}
      <div className="d-flex align-items-center justify-content-end gap-1" style={{ width: 128, flexShrink: 0 }}>
        <button
          onClick={() => onPreview(file)}
          title="Preview file"
          className="btn btn-sm d-flex align-items-center justify-content-center"
          style={{
            width: 28, height: 28, padding: 0,
            fontSize: 13, color: '#64748b',
            background: 'transparent', border: 'none', borderRadius: btnRadius,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#1e293b'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
        >
          <i className="bi bi-eye" />
        </button>

        <a
          href={file.public_url ? `${file.public_url}?dl=1` : '#'}
          title="Download file"
          className="btn btn-sm d-flex align-items-center justify-content-center"
          style={{
            width: 28, height: 28, padding: 0,
            fontSize: 13, color: '#64748b',
            background: 'transparent', border: 'none', borderRadius: btnRadius,
            textDecoration: 'none',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#1e293b'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
        >
          <i className="bi bi-download" />
        </a>

        <button
          onClick={() => onCopy(file)}
          title="Copy public link"
          className="btn btn-sm d-flex align-items-center justify-content-center"
          style={{
            width: 28, height: 28, padding: 0,
            fontSize: 13, color: copied ? '#059669' : '#64748b',
            background: 'transparent', border: 'none', borderRadius: btnRadius,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <i className={`bi ${copied ? 'bi-check2' : 'bi-link-45deg'}`} />
        </button>

        <button
          onClick={() => !isCustomer && onDelete(file)}
          aria-disabled={isCustomer}
          title={isCustomer
            ? "Customer upload — delete the table row that uses this file to remove it"
            : "Delete file"}
          className="btn btn-sm d-flex align-items-center justify-content-center"
          style={{
            width: 28, height: 28, padding: 0,
            fontSize: 13, color: isCustomer ? '#cbd5e1' : '#dc2626',
            background: 'transparent', border: 'none', borderRadius: btnRadius,
            cursor: isCustomer ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={e => { if (!isCustomer) e.currentTarget.style.background = '#fef2f2'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <i className={`bi ${isCustomer ? 'bi-lock-fill' : 'bi-trash3'}`} />
        </button>
      </div>
    </div>
  );
}

// ── TextPreview ───────────────────────────────────────────────────────────────
// Fetches a text-type file with JS and renders it inline so the browser's
// default behavior for text/csv (which Chrome treats as "download, do not
// display") cannot trigger an unintended download. Caps content at
// TEXT_PREVIEW_MAX_BYTES so a 50 MB log file can't lock the browser.
const TEXT_PREVIEW_MAX_BYTES = 1024 * 1024;   // 1 MiB

function TextPreview({ url, fallback }) {
  const [state, setState] = useState({ status: 'loading', text: '', truncated: false });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', text: '', truncated: false });

    fetch(url, { credentials: 'omit' })
      .then(async resp => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        // Read the body as a length-capped UTF-8 string. We don't trust
        // Content-Length (it may be missing or wrong) — slice on actual bytes.
        const blob = await resp.blob();
        const truncated = blob.size > TEXT_PREVIEW_MAX_BYTES;
        const slice = truncated ? blob.slice(0, TEXT_PREVIEW_MAX_BYTES) : blob;
        const text = await slice.text();
        if (!cancelled) setState({ status: 'ok', text, truncated });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', text: '', truncated: false });
      });

    return () => { cancelled = true; };
  }, [url]);

  if (state.status === 'loading') {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 200, color: '#64748b' }}>
        <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
        <span style={{ fontSize: 13 }}>Loading preview…</span>
      </div>
    );
  }
  if (state.status === 'error') return fallback;

  return (
    <div style={{ background: '#f8fafc', maxHeight: '65vh', overflow: 'auto' }}>
      {state.truncated && (
        <div style={{ padding: '8px 16px', fontSize: 12, color: '#92400e', background: '#fef3c7', borderBottom: '1px solid #fde68a' }}>
          Preview truncated — showing the first {Math.round(TEXT_PREVIEW_MAX_BYTES / 1024)} KB. Download the file to see the rest.
        </div>
      )}
      <pre
        style={{
          margin: 0, padding: '16px 18px',
          fontFamily: 'Menlo, Monaco, "Courier New", monospace',
          fontSize: 12.5, lineHeight: 1.55, color: '#1e293b',
          whiteSpace: 'pre', tabSize: 4,
        }}
      >
        {state.text}
      </pre>
    </div>
  );
}


// ── FilePreviewModal ──────────────────────────────────────────────────────────

function FilePreviewModal({ file, onClose, cardRadius, btnRadius, theme }) {
  const primary = theme?.primary || '#1e293b';
  const meta = FILE_TYPE_META[file.file_type] || FILE_TYPE_META.other;
  const url = file.public_url || '';

  // Extract original extension from the stored filename (not display_name which user may have changed)
  const fileExt = (file.filename || file.display_name || '').split('.').pop().toLowerCase();

  // Browser-native video formats only — others (avi, mov, wmv, flv) trigger download
  const BROWSER_VIDEO_EXTS = new Set(['mp4', 'webm', 'ogg']);
  // Browser-native audio formats
  const BROWSER_AUDIO_EXTS = new Set(['mp3', 'wav', 'ogg', 'aac', 'm4a']);
  const DownloadPanel = ({ message }) => (
    <div
      className="d-flex flex-column align-items-center justify-content-center gap-4"
      style={{ padding: '48px 24px', background: meta.bg, minHeight: 200 }}
    >
      <i className={`bi ${meta.icon}`} style={{ fontSize: 56, color: meta.color }} />
      <span style={{ fontSize: 13, color: '#64748b', textAlign: 'center', maxWidth: 320 }}>
        {message}
      </span>
      <a
        href={`${url}?dl=1`}
        className="btn btn-sm"
        style={{
          background: primary, color: '#fff',
          borderRadius: btnRadius, fontSize: 13, fontWeight: 600,
          padding: '8px 20px', border: 'none', textDecoration: 'none',
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}
      >
        <i className="bi bi-download" />
        Download File
      </a>
    </div>
  );

  const renderPreview = () => {
    if (!url) {
      return (
        <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3">
          <i className="bi bi-link-45deg-slash" style={{ fontSize: 48, color: '#cbd5e1' }} />
          <span style={{ fontSize: 14, color: '#64748b' }}>No public link available for this file.</span>
        </div>
      );
    }

    switch (file.file_type) {
      case 'image':
        return (
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#0f172a', minHeight: 320, maxHeight: '65vh', overflow: 'hidden',
            }}
          >
            <img
              src={url}
              alt={file.display_name}
              style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', display: 'block' }}
            />
          </div>
        );

      case 'video':
        if (!BROWSER_VIDEO_EXTS.has(fileExt)) {
          return (
            <DownloadPanel message={`${fileExt.toUpperCase()} files cannot be played in the browser. Download to view.`} />
          );
        }
        return (
          <div style={{ background: '#000', display: 'flex', justifyContent: 'center' }}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              src={url}
              controls
              style={{ maxWidth: '100%', maxHeight: '65vh', display: 'block' }}
            />
          </div>
        );

      case 'audio':
        if (!BROWSER_AUDIO_EXTS.has(fileExt)) {
          return (
            <DownloadPanel message={`${fileExt.toUpperCase()} audio cannot be played in the browser. Download to listen.`} />
          );
        }
        return (
          <div
            className="d-flex flex-column align-items-center justify-content-center gap-4"
            style={{ padding: '40px 24px', background: meta.bg, minHeight: 180 }}
          >
            <i className={`bi ${meta.icon}`} style={{ fontSize: 56, color: meta.color }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', textAlign: 'center' }}>
              {file.display_name}
            </span>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio src={url} controls style={{ width: '100%', maxWidth: 380 }} />
          </div>
        );

      case 'document':
        // Only PDF can be rendered inline by the browser
        if (fileExt === 'pdf') {
          return (
            <embed
              src={url}
              type="application/pdf"
              style={{ width: '100%', height: '65vh', display: 'block', border: 'none' }}
            />
          );
        }
        // Office / OpenDocument formats always download — browser cannot render them
        return (
          <DownloadPanel
            message={`${fileExt.toUpperCase()} files cannot be previewed in the browser. Download to open in your office application.`}
          />
        );

      case 'text':
        // CSV/TSV/log/json/etc. cannot be safely rendered via <iframe src=...>:
        // Chrome treats text/csv as a download MIME, so an iframe load would
        // trigger an unintended file download. We fetch the content with JS
        // and render it ourselves — no browser MIME quirks involved.
        return <TextPreview url={url} fallback={<DownloadPanel message="Could not load preview. Download to view the file." />} />;

      case 'archive':
        return (
          <DownloadPanel message="Archive files cannot be previewed in the browser. Download to extract." />
        );

      default:
        return (
          <DownloadPanel message="Preview not available for this file type." />
        );
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1060,
        background: 'rgba(15,23,42,0.6)',
        backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: cardRadius,
          width: '100%',
          maxWidth: 800,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 80px rgba(0,0,0,0.28)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          className="d-flex align-items-center justify-content-between px-4 py-3"
          style={{ borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}
        >
          <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                width: 32, height: 32,
                background: meta.bg,
                borderRadius: btnRadius,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <i className={`bi ${meta.icon}`} style={{ fontSize: 15, color: meta.color }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14, fontWeight: 700, color: '#1e293b',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
                title={file.display_name}
              >
                {file.display_name}
              </div>
              <div className="d-flex align-items-center gap-2 mt-1">
                {fileExt && (
                  <span
                    style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
                      padding: '2px 6px', borderRadius: `calc(${btnRadius} / 2)`,
                      background: meta.bg, color: meta.color,
                      textTransform: 'uppercase', lineHeight: 1.2,
                    }}
                  >
                    {fileExt}
                  </span>
                )}
                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                  {formatBytes(file.file_size)} · {formatDate(file.uploaded_at)}
                </span>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 ms-3" style={{ flexShrink: 0 }}>
            {url && (
              <>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm d-flex align-items-center gap-1"
                  style={{
                    fontSize: 12, fontWeight: 600,
                    background: '#f1f5f9', color: '#475569',
                    border: 'none', borderRadius: btnRadius,
                    padding: '6px 12px', textDecoration: 'none',
                  }}
                >
                  <i className="bi bi-box-arrow-up-right" style={{ fontSize: 11 }} />
                  Open
                </a>
                <a
                  href={`${url}?dl=1`}
                  className="btn btn-sm d-flex align-items-center gap-1"
                  style={{
                    fontSize: 12, fontWeight: 600,
                    background: '#f1f5f9', color: '#475569',
                    border: 'none', borderRadius: btnRadius,
                    padding: '6px 12px', textDecoration: 'none',
                  }}
                >
                  <i className="bi bi-download" style={{ fontSize: 11 }} />
                  Download
                </a>
              </>
            )}
            <button
              onClick={onClose}
              className="btn p-0 d-flex align-items-center justify-content-center"
              style={{ color: '#94a3b8', fontSize: 18, lineHeight: 1, width: 32, height: 32 }}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
        </div>

        {/* Preview area */}
        <div style={{ overflow: 'auto', flex: 1 }}>
          {renderPreview()}
        </div>
      </div>
    </div>
  );
}

// ── UploadModal ───────────────────────────────────────────────────────────────

function UploadModal({ applicationId, onClose, onSuccess, cardRadius, btnRadius, theme }) {
  const [dragOver, setDragOver]         = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [displayName, setDisplayName]   = useState('');
  const [uploading, setUploading]       = useState(false);
  const [progress, setProgress]         = useState(0);
  const [errorMsg, setErrorMsg]         = useState('');
  const fileInputRef = useRef();

  const primary = theme?.primary || '#1e293b';

  const pickFile = (f) => {
    setSelectedFile(f);
    setDisplayName(f.name);
    setErrorMsg('');
    setProgress(0);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) pickFile(f);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setErrorMsg('');
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile, selectedFile.name);
      formData.append('display_name', displayName || selectedFile.name);

      // Use XMLHttpRequest to track upload progress
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            let msg = 'Upload failed.';
            try { msg = JSON.parse(xhr.responseText)?.error || msg; } catch {}
            reject(new Error(msg));
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload.'));

        // Build URL manually — APIHelper's apiCall doesn't expose XHR progress
        const base = envConfig.backendApiUrl?.replace(/\/$/, '') || '';
        xhr.open('POST', `${base}/arcee/application/${applicationId}/files/upload/`);

        // Must send session cookie alongside the Bearer token
        xhr.withCredentials = true;

        // Copy auth headers from apiHelper (uses cached token, does not re-fetch)
        apiHelper.getAuthHeaders().then(headers => {
          Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
          xhr.send(formData);
        }).catch(reject);
      });

      setProgress(100);
      onSuccess();
    } catch (err) {
      setErrorMsg(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="modal d-flex align-items-center justify-content-center"
      style={{
        position: 'fixed', inset: 0, zIndex: 1055,
        background: 'rgba(15,23,42,0.45)',
        backdropFilter: 'blur(2px)',
      }}
      onClick={e => { if (e.target === e.currentTarget && !uploading) onClose(); }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: cardRadius,
          width: '100%',
          maxWidth: 480,
          margin: '0 16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          className="d-flex align-items-center justify-content-between px-4 py-3"
          style={{ borderBottom: '1px solid #f1f5f9' }}
        >
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-cloud-upload" style={{ fontSize: 18, color: primary }} />
            <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>Upload File</span>
          </div>
          {!uploading && (
            <button
              onClick={onClose}
              className="btn p-0"
              style={{ color: '#94a3b8', fontSize: 18, lineHeight: 1 }}
            >
              <i className="bi bi-x-lg" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="px-4 pt-4 pb-3">
          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? primary : '#cbd5e1'}`,
              borderRadius: cardRadius,
              background: dragOver ? '#f8faff' : '#f8fafc',
              padding: '28px 16px',
              textAlign: 'center',
              cursor: uploading ? 'default' : 'pointer',
              transition: 'border-color 0.2s, background 0.2s',
            }}
          >
            {selectedFile ? (
              <div>
                <i
                  className={`bi ${FILE_TYPE_META[selectedFile.type?.split('/')[0]] ? 'bi-file-earmark-check' : 'bi-file-earmark-check'}`}
                  style={{ fontSize: 36, color: primary }}
                />
                <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', marginTop: 8 }}>
                  {selectedFile.name}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>{formatBytes(selectedFile.size)}</div>
                {!uploading && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Click to change file</div>
                )}
              </div>
            ) : (
              <div>
                <i className="bi bi-cloud-arrow-up" style={{ fontSize: 42, color: '#94a3b8' }} />
                <div style={{ fontWeight: 600, fontSize: 13, color: '#475569', marginTop: 8 }}>
                  Drag &amp; drop your file here
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>or click to browse</div>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              disabled={uploading}
            />
          </div>

          {/* Display Name */}
          {selectedFile && (
            <div className="mt-3">
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' }}>
                Display Name
              </label>
              <input
                type="text"
                className="form-control"
                style={{ fontSize: 13 }}
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                disabled={uploading}
                placeholder="Optional label for this file"
              />
            </div>
          )}

          {/* Progress bar */}
          {uploading && (
            <div className="mt-3">
              <div className="d-flex justify-content-between mb-1">
                <span style={{ fontSize: 12, color: '#475569' }}>Uploading…</span>
                <span style={{ fontSize: 12, color: '#475569' }}>{progress}%</span>
              </div>
              <div
                style={{ height: 6, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${progress}%`,
                    background: primary,
                    borderRadius: 99,
                    transition: 'width 0.2s',
                  }}
                />
              </div>
            </div>
          )}

          {/* Error */}
          {errorMsg && (
            <div
              className="d-flex align-items-center gap-2 mt-3 p-2"
              style={{ background: '#fef2f2', borderRadius: btnRadius, fontSize: 12, color: '#dc2626' }}
            >
              <i className="bi bi-exclamation-circle" />
              {errorMsg}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="d-flex justify-content-end gap-2 px-4 py-3"
          style={{ borderTop: '1px solid #f1f5f9' }}
        >
          <button
            className="btn btn-sm"
            onClick={onClose}
            disabled={uploading}
            style={{
              fontSize: 13, fontWeight: 600,
              background: '#f1f5f9', color: '#475569',
              border: 'none', borderRadius: btnRadius,
              padding: '7px 18px',
            }}
          >
            Cancel
          </button>
          <button
            className="btn btn-sm"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            style={{
              fontSize: 13, fontWeight: 600,
              background: !selectedFile || uploading ? '#94a3b8' : primary,
              color: '#fff',
              border: 'none', borderRadius: btnRadius,
              padding: '7px 20px',
              minWidth: 110,
            }}
          >
            {uploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" style={{ width: 12, height: 12 }} />
                Uploading…
              </>
            ) : 'Upload File'}
          </button>
        </div>
      </div>
    </div>
  );
}
